'use client';
import React from 'react';
import PropTypes from 'prop-types';
import { ResponsiveImage } from '@web/molecules';
import { Heading } from '@web/atoms';
import PawIcon from '../../images/Icons/paw-icon.svg';
import CheckIcon from '../../images/Icons/check-icon.svg';
import XIcon from '../../images/Icons/x-icon.svg';
import OllieIcon from '../../images/ollie-logo-lilac.svg';
import {
  StyledComparisonChart,
  ScrollWrapper,
  ScrollContainer,
  Content,
  ChartRowHeaders,
  ChartColumn,
  ComparisonColumnItem,
  ColumnHeader,
  ColumnsContainer,
  StickyColumn,
  ScrollableColumns,
  Subheader,
} from './styles.js';
import { ComparisonChartItem } from './ComparisonChartItem';

export const ComparisonChart = ({
  heading,
  description,
  backgroundColor = 'background.base',
  textColor = 'text.primary',
  rows,
  columns,
  level,
  typography = 'heading2',
}) => {
  const stickyColumnIndex = columns.findIndex(col => col.highlighted);
  const stickyColumn = columns[stickyColumnIndex];

  const remainingColumns = columns.reduce((acc, column, i) => {
    if (i !== stickyColumnIndex) {
      acc.push({ column, originalIndex: i });
    }
    return acc;
  }, []);

  return (
    <StyledComparisonChart background={backgroundColor}>
      <Heading
        headingLevel={level}
        typography={typography}
        headingText={heading}
        position="center"
        verticalPadding={2}
        backgroundColor={backgroundColor}
        fontColor={textColor}
      />
      <Subheader>{description}</Subheader>

      <ScrollWrapper items={columns.length}>
        {rows && (
          <ChartRowHeaders items={columns.length}>
            <ComparisonChartItem key="empty" header="" subheader="" />
            {rows.map((feature, i) => (
              <ComparisonChartItem
                key={i}
                header={feature.header}
                subheader={feature.subheader}
              />
            ))}
          </ChartRowHeaders>
        )}

        {stickyColumn && (
          <StickyColumn items={columns.length}>
            <ChartColumn highlighted={true} items={columns.length}>
              <ComparisonColumnItem>
                <ResponsiveImage
                  src={OllieIcon.src}
                  alt="Ollie logo"
                  width={OllieIcon.width}
                  height={OllieIcon.height}
                />
              </ComparisonColumnItem>
              {rows.map((row, j) => (
                <ComparisonColumnItem highlighted={true} key={j}>
                  {row.comparisonRowMarkers[stickyColumnIndex]?.on === true ? (
                    <ResponsiveImage
                      src={PawIcon.src}
                      alt="paw icon"
                      width={PawIcon.width}
                      height={PawIcon.height}
                    />
                  ) : (
                    <ResponsiveImage
                      src={XIcon.src}
                      alt="x icon"
                      width={XIcon.width}
                      height={XIcon.height}
                    />
                  )}
                </ComparisonColumnItem>
              ))}
            </ChartColumn>
          </StickyColumn>
        )}

        <ScrollContainer>
          <Content>
            {columns && (
              <ColumnsContainer>
                <ScrollableColumns>
                  {remainingColumns.map(({ column, originalIndex }) => {
                    return (
                      <ChartColumn key={originalIndex} items={columns.length}>
                        <ComparisonColumnItem>
                          <ColumnHeader as={column.level || 'h2'}>
                            {column.label}
                          </ColumnHeader>
                        </ComparisonColumnItem>
                        {rows.map((row, j) => (
                          <ComparisonColumnItem key={j}>
                            {row.comparisonRowMarkers[originalIndex]?.on ===
                            true ? (
                              <ResponsiveImage
                                src={CheckIcon.src}
                                alt="check icon"
                                width={CheckIcon.width}
                                height={CheckIcon.height}
                              />
                            ) : (
                              <ResponsiveImage
                                src={XIcon.src}
                                alt="x icon"
                                width={XIcon.width}
                                height={XIcon.height}
                              />
                            )}
                          </ComparisonColumnItem>
                        ))}
                      </ChartColumn>
                    );
                  })}
                </ScrollableColumns>
              </ColumnsContainer>
            )}
          </Content>
        </ScrollContainer>
      </ScrollWrapper>
    </StyledComparisonChart>
  );
};

ComparisonChart.displayName = 'ComparisonChart';

ComparisonChart.propTypes = {
  heading: PropTypes.string.isRequired,
  description: PropTypes.string,
  textColor: PropTypes.string,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      subheader: PropTypes.string,
      comparisonRowMarkers: PropTypes.arrayOf(
        PropTypes.shape({
          on: PropTypes.bool,
          off: PropTypes.bool,
        }),
      ),
    }),
  ).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      highlighted: PropTypes.bool,
    }),
  ).isRequired,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  typography: PropTypes.string,
};
